import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../context/AppContext";
import Sidebar from "../components/Sidebar";
import PageShell from "../components/PageShell";
import { FitTag, ScoreBadge } from "../components/atoms";
import ExportModal from "../components/ExportModal";
import { COMPANY_MASTER_COLUMNS, DEFAULT_KEYS } from "../config/exportColumns";
import { getCompanies, getCompanyFacets, getAllCompanies, getEventCategories } from "../services/api";
import { Search, LayoutGrid, List, X, Download, AlertCircle } from "lucide-react";
import { FilterBar, FilterSelect, FilterClear } from "../components/FilterBar";
import Pagination from "../components/Pagination";
import Refreshing from "../components/Refreshing";
import { useServerList, useDebounced } from "../hooks/useServerList";

const F = "'Montserrat',sans-serif";
const M = "'Montserrat',sans-serif";

// Rows per page. The list is served a page at a time, so this is also the most
// rows the browser ever holds — it does not cap what the filters can find.
const PAGE_SIZE = 50;

// Fields used to compute profile completeness (12 fields, each worth ~8%)
const COMPLETENESS_FIELDS = [
  "company_name",    // identity
  "hq_country",      // location
  "hq_city",         // location detail
  "website",         // digital presence
  "linkedin_page",   // digital presence
  "email",           // contact
  "phone",           // contact
  "physical_address",// contact
  "core_products",   // product intelligence
  "target_customers",// market intelligence
  "target_market",   // market intelligence
  "company_type",    // classification
];

// Placeholder/sentinel strings enrichment writes when a value couldn't be found.
// These must NOT count toward completeness (the profile UI shows them as blank /
// "Not available"), otherwise a company with e.g. email="Unknown" scores 100%.
const PLACEHOLDER_VALUES = new Set([
  "", "unknown", "n/a", "na", "none", "null", "not available", "not applicable", "-", "—",
]);
const hasRealValue = (v) => v != null && !PLACEHOLDER_VALUES.has(String(v).trim().toLowerCase());

// Module-level so it is not re-created on every render
function mapCompany(co) {
  // Split only on pipe — commas appear within product names and must not split them
  const splitPipe = (s) =>
    s ? s.split("|").map((x) => x.trim()).filter(Boolean) : [];

  const toFitLabel = (v) => {
    if (v == null || v === "") return "—";
    if (typeof v === "number") return v >= 70 ? "High" : v >= 40 ? "Medium" : "Low";
    const s = String(v).toLowerCase();
    return s === "high" ? "High" : s === "medium" ? "Medium" : s === "low" ? "Low" : "—";
  };

  // The API computes this too, and its value is the one the Completeness filter
  // and sort act on — so prefer it, and fall back only if it is absent.
  const computedCompleteness = Math.round(
    (COMPLETENESS_FIELDS.filter((f) => hasRealValue(co[f])).length / COMPLETENESS_FIELDS.length) * 100
  );

  const secondaryEventDetails = splitPipe(co.secondary_event_details);
  const secondaryEventReasonings = splitPipe(co.secondary_event_reasonings);
  const primaryEventName = co.primary_event_name || "";
  const primaryEventCategory = co.primary_event_category || "";
  const primaryEventReasoning = co.primary_event_fit_logic || "";
  const primaryOpportunity =
    hasRealValue(primaryEventName) && hasRealValue(primaryEventCategory)
      ? `${primaryEventName}: ${primaryEventCategory}`
      : "";
  const primaryReasoning =
    primaryOpportunity && hasRealValue(primaryEventReasoning)
      ? `${primaryEventName}: ${primaryEventReasoning}`
      : "";

  return {
    ...co,
    id:                  co.company_id,
    year:                co.year ?? (co.show_id ? (parseInt(co.show_id.slice(-4)) || null) : null),
    name:                co.company_name  || "—",
    country:             co.hq_country    || "—",
    region:              co.country_region || "—",
    priority:            co.priority_score ?? 0,
    eventFit:            toFitLabel(co.primary_event_fit),
    exportReady:         toFitLabel(co.export_readiness_score),
    coreProducts:        splitPipe(co.core_products),
    categories:          splitPipe(co.core_products),  // alias used for search
    targetCustomers:     splitPipe(co.target_customers),
    targetMarkets:       splitPipe(co.target_market),
    type:                co.company_type  || "—",
    // is_new/is_returning are per competitor-show edition (see ddl: "did not
    // appear in (show_id, year - 1)"), not a company's history with ITE. Both
    // NULL means the edition hasn't been enriched yet — show nothing, not a
    // status we can't support.
    iteStatus:           co.is_new ? "New" : co.is_returning ? "Returning" : "—",
    // Computed by the API across every year, not just the rows on this page —
    // a company is Returning if an earlier edition of it exists anywhere.
    derivedStatus:       co.derived_status || "Regular",
    countryStatus:       co.country_status  || null,
    recAction:           co.priority_reason || "—",
    completeness:        co.completeness ?? computedCompleteness,
    primaryEventName:    primaryEventName || null,
    eventFitReason:      co.primary_event_fit_logic || "",
    exportReadinessLogic: co.export_readiness_logic || "",
    pastEvents:          co.past_events || "",
    crossEventOpportunities: [primaryOpportunity, ...secondaryEventDetails].filter(Boolean).join(" | "),
    crossEventReasoning: [primaryReasoning, ...secondaryEventReasonings].filter(Boolean).join(" | "),
    showName:            co.show_name || null,
    secondaryEventNames: co.secondary_event_names
      ? co.secondary_event_names.split("|").map((s) => s.trim()).filter(Boolean)
      : [],
  };
}

export default function CompaniesPage() {
  const {
    nav, selectedEvent, changeEvent,
    currentRole, setCurrentRole, currentUser,
    notifs, setNotifs, notifOpen, setNotifOpen,
    companies, setCompanies,
    navToCompany, showToast,
    companiesYear, setCompaniesYear,
    companiesUi, setCompaniesUi,
  } = useApp();

  const [exportCfg, setExportCfg] = useState(null);

  // Filters/search/sort/view initialise from the persisted slot (restored on
  // return from a company detail); a fresh year hand-off takes precedence.
  const [viewMode,    setViewMode]    = useState(() => companiesUi?.viewMode ?? "card");
  const [search,      setSearch]      = useState(() => companiesUi?.search ?? "");
  const [filters,     setFilters]     = useState(() =>
    // Full context pushed from Portfolio "View X companies" takes highest priority
    companiesUi?.filters != null ? companiesUi.filters :
    // Legacy year-only hand-off (other callers)
    companiesYear != null ? { year: String(companiesYear), country_status: "Friendly" } :
    { country_status: "Friendly" }
  );
  const [sort,        setSort]        = useState(() => companiesUi?.sort ?? { by: "name", dir: "asc" });
  // Selection is per page — the rows off-screen were never fetched, so "select
  // all" can only mean the ones visible.
  const [selectedIds, setSelectedIds] = useState(new Set());

  const bodyRef   = useRef(null);
  const allChkRef = useRef(null);

  // Clear the context slot after consuming it on mount
  useEffect(() => {
    if (companiesYear != null) setCompaniesYear(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist filters/search/sort/view so they survive navigating into a company
  // detail and back (this page unmounts; AppContext holds the state).
  useEffect(() => {
    setCompaniesUi({ filters, search, sort, viewMode });
  }, [filters, search, sort, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Data fetching ─────────────────────────────────────────────────────────────
  // Filtering, sorting and paging all happen in SQL. The page holds one page of
  // rows, so nothing here may be derived from "all the companies" — the header
  // counts, the dropdown options and the returning/new status come from the API.

  const debouncedSearch = useDebounced(search, 400);

  // The query the list and the dropdowns are both filtered by. Keys match the
  // API's parameter names so it can be forwarded as-is.
  const query = React.useMemo(() => {
    const q = {};
    if (filters.year)           q.year           = filters.year;
    if (filters.ite_event)      q.ite_event      = filters.ite_event;
    if (filters.category)       q.category       = filters.category;
    if (filters.show)           q.show_name      = filters.show;
    if (filters.country)        q.country        = filters.country;
    if (filters.country_status) q.country_status = filters.country_status;
    if (filters.company_type)   q.company_type   = filters.company_type;
    if (filters.hq_type)        q.hq_type        = filters.hq_type;
    if (filters.priority)       q.priority_band  = filters.priority === "70–84" ? "70-84" : filters.priority;
    if (filters.completeness)   q.completeness   =
      filters.completeness === "Complete (≥70%)" ? "complete" : "incomplete";
    if (filters.website)        q.website_status = filters.website === "Has website" ? "has" : "empty";
    if (debouncedSearch)        q.search         = debouncedSearch;
    q.sort_by  = sort.by || "priority";
    q.sort_dir = sort.dir || "desc";
    return q;
  }, [filters, debouncedSearch, sort.by, sort.dir]);

  const { items, total, meta, offset, setOffset, loading, initialLoading, error: fetchError, refresh } =
    useServerList(getCompanies, query, PAGE_SIZE);

  // Keep the page's rows in shared context so the company detail view can read them.
  const rows = React.useMemo(() => items.map(mapCompany), [items]);
  useEffect(() => { setCompanies(rows); }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dropdown options come from the server, narrowed by the other active filters —
  // the same cascading behaviour as before, but over every company rather than
  // whichever ones happen to be on this page.
  // Sorting and paging cannot add or remove a value from the result set, so the
  // facet query drops them. Keying the effect on the serialised query rather than
  // on `query` itself is what stops a sort click costing a second round trip that
  // returns exactly what it returned before.
  const [facets, setFacets] = useState({});
  const facetQuery = React.useMemo(() => {
    const { sort_by, sort_dir, ...rest } = query;
    return rest;
  }, [query]);
  const facetKey = JSON.stringify(facetQuery);

  useEffect(() => {
    let stale = false;
    getCompanyFacets(facetQuery)
      .then((f) => { if (!stale) setFacets(f); })
      .catch(() => { if (!stale) setFacets({}); });
    return () => { stale = true; };   // a slow reply must not overwrite a newer one
  }, [facetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Category options come from the selected event's taxonomy (config), not the data.
  // Fetch on event change; drop a category selection that isn't valid for the new event.
  const [categoryOpts, setCategoryOpts] = useState([]);
  useEffect(() => {
    if (!filters.ite_event) {
      setCategoryOpts([]);
      if (filters.category) setFilters((f) => { const n = { ...f }; delete n.category; return n; });
      return;
    }
    let stale = false;
    getEventCategories(filters.ite_event)
      .then((cats) => {
        if (stale) return;
        const list = Array.isArray(cats) ? cats : [];
        setCategoryOpts(list);
        if (filters.category && !list.includes(filters.category))
          setFilters((f) => { const n = { ...f }; delete n.category; return n; });
      })
      .catch(() => { if (!stale) setCategoryOpts([]); });
    return () => { stale = true; };
  }, [filters.ite_event]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setSelectedIds(new Set()); }, [viewMode, offset]);

  // Default to the most recent year on first load, unless a year was handed in.
  const yearDefaulted = useRef(false);
  useEffect(() => {
    if (yearDefaulted.current || filters.year || !facets.year?.length) return;
    yearDefaulted.current = true;
    const thisYear = String(new Date().getFullYear());
    const best = facets.year.includes(thisYear) ? thisYear : facets.year[0];
    if (best) setFilters((f) => ({ ...f, year: best }));
  }, [facets.year]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtering, dedupe, returning-status and sorting are all done by the API now,
  // so this page renders exactly the rows it was given, in the order given.
  const sorted = rows;

  const CS_ORDER = ["Friendly", "Unfriendly", "Unknown"];

  const toggleSort = (col) =>
    setSort((s) =>
      s.by === col
        ? { by: col, dir: s.dir === "asc" ? "desc" : "asc" }
        : { by: col, dir: "asc" }
    );

  // ── Selection ─────────────────────────────────────────────────────────────────

  const toggleOne = (id, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleAll = () =>
    setSelectedIds(
      selectedIds.size === sorted.length ? new Set() : new Set(sorted.map((c) => c.id))
    );

  useEffect(() => {
    if (allChkRef.current)
      allChkRef.current.indeterminate =
        selectedIds.size > 0 && selectedIds.size < sorted.length;
  }, [selectedIds.size, sorted.length]);

  // ── KPI values ────────────────────────────────────────────────────────────────
  // Aggregated by the API over everything matching the filters. Counting the rows
  // on screen would report one page's worth and read as the whole result.

  const isFiltered   = search || Object.keys(filters).length > 0;
  const displayTotal = total;
  const highPriority = meta.high_priority ?? 0;
  const countryCount = meta.country_count ?? 0;
  const hasFilters   = Object.keys(filters).length > 0;

  // Export covers the whole result set, so it fetches past the current page.
  const [exporting, setExporting] = useState(false);
  const openExport = () => {
    setExporting(true);
    const { sort_by, sort_dir, ...scope } = query;
    getAllCompanies(query)
      .then((all) => setExportCfg({
        title: "Export Company Master",
        columns: COMPANY_MASTER_COLUMNS,
        defaultKeys: DEFAULT_KEYS.companyMaster,
        rows: all.map(mapCompany),
        filenameBase: "company_master",
        exportType: "company_master",
        scope: { filters },
      }))
      .catch(() => showToast?.("Could not prepare the export", "error"))
      .finally(() => setExporting(false));
  };

  // ── Small sub-components ──────────────────────────────────────────────────────

  const CS_STYLE = {
    Friendly:   { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
    Unfriendly: { color: "#A00F1A", bg: "#fdecec", border: "#fbd5d5" },
    Unknown:    { color: "#818084", bg: "#FAFAFA", border: "#D8DADC" },
  };
  const CountryStatusTag = ({ status }) => {
    if (!status) return null;
    const s = CS_STYLE[status] || CS_STYLE.Unknown;
    return (
      <span style={{
        display:"inline-block", fontSize:8, fontWeight:600, padding:"1px 6px",
        borderRadius:20, color:s.color, background:s.bg, border:`1px solid ${s.border}`,
        whiteSpace:"nowrap", letterSpacing:0.1,
      }}>
        {status}
      </span>
    );
  };

  const SortArrow = ({ col }) =>
    sort.by !== col
      ? <span style={{ opacity: 0.3, marginLeft: 3 }}>↕</span>
      : <span style={{ marginLeft: 3, color: "#E8431A" }}>{sort.dir === "asc" ? "↑" : "↓"}</span>;

  const CompBar = ({ pct }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <div style={{ width: 40, height: 4, background: "#F4F4F5", borderRadius: 2, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${pct}%`, borderRadius: 2,
          background: pct >= 70 ? "#22c55e" : pct >= 40 ? "#F18F18" : "#C1121F",
        }} />
      </div>
      <span style={{
        fontSize: 9, fontWeight: 600,
        color: pct >= 70 ? "#16a34a" : pct >= 40 ? "#C9760A" : "#C1121F",
      }}>{pct}%</span>
    </div>
  );

  const EventChips = ({ names }) => {
    if (!names.length) return <span style={{ fontSize: 9, color: "#D8DADC" }}>—</span>;
    const shown = names.slice(0, 2);
    const rest  = names.length - 2;
    return (
      <div style={{ display: "flex", flexWrap: "nowrap", gap: 3, overflow: "hidden", alignItems: "center" }}>
        {shown.map((ev, i) => (
          <span key={i} style={{
            fontSize: 9, padding: "2px 7px", borderRadius: 4,
            background: "#f0f4ff", color: "#5F1156", fontWeight: 500,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            maxWidth: 90, display: "inline-block",
          }}>{ev}</span>
        ))}
        {rest > 0 && (
          <span style={{ fontSize: 9, color: "#818084", whiteSpace: "nowrap" }}>
            +{rest} more
          </span>
        )}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <PageShell>
      <style>{`
        *::-webkit-scrollbar { width: 5px }
        *::-webkit-scrollbar-thumb { background: #D8DADC; border-radius: 3px }
        table.ct { width: 100%; border-collapse: collapse; font-size: 11px }
        table.ct th {
          text-align: left; padding: 8px 10px; font-size: 9px; font-weight: 700;
          color: #818084; text-transform: uppercase; letter-spacing: 0.4px;
          border-bottom: 2px solid #D8DADC; background: #FAFAFA;
          position: sticky; top: 0; z-index: 1; user-select: none;
        }
        table.ct th.sortable { cursor: pointer }
        table.ct th.sortable:hover { background: #F4F4F5 }
        table.ct td { padding: 8px 10px; border-bottom: 1px solid #F4F4F5; vertical-align: middle }
        table.ct tbody tr:hover td { background: #FAFAFA }
      `}</style>

      <Sidebar
        active="companies" onNav={nav} selectedEvent={selectedEvent} onChangeEvent={changeEvent}
        notifs={notifs} setNotifs={setNotifs} notifOpen={notifOpen} setNotifOpen={setNotifOpen}
        currentRole={currentRole} setCurrentRole={setCurrentRole}
      />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div style={{ padding: "12px 28px 10px", background: "#fff", borderBottom: "1px solid #D8DADC" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>Company Master</div>
              <div style={{ fontSize: 11, color: "#818084", marginTop: 2 }}>
                Browse enriched company profiles, event fit scores, and export readiness intelligence
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                onClick={openExport}
                disabled={exporting}
                style={{
                  display: "flex", alignItems: "center", gap: 5, padding: "5px 12px",
                  borderRadius: 6, border: "1px solid #D8DADC", background: "#fff",
                  fontSize: 11, fontWeight: 600, cursor: exporting ? "default" : "pointer",
                  fontFamily: F, color: exporting ? "#818084" : "#3F3F46",
                }}>
                <Download size={12} /> {exporting ? "Preparing…" : "Export"}
              </button>
              <div style={{ display: "flex", background: "#F4F4F5", borderRadius: 6, padding: 2 }}>
                {[["card", <LayoutGrid size={13} />, "Cards"], ["table", <List size={13} />, "Table"]].map(([m, ic, lb]) => (
                  <button key={m}
                    onClick={() => { setViewMode(m); bodyRef.current && (bodyRef.current.scrollTop = 0); }}
                    style={{
                      padding: "5px 12px", borderRadius: 5, border: "none", fontSize: 11,
                      fontWeight: 600, cursor: "pointer", fontFamily: F, display: "flex", alignItems: "center", gap: 4,
                      background: viewMode === m ? "#fff" : "transparent",
                      color: viewMode === m ? "#26262A" : "#818084",
                      boxShadow: viewMode === m ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                    }}>
                    {ic} {lb}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ position: "relative", marginTop: 10 }}>
            <input
              type="text"
              placeholder="Search company name, website, products, country…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", padding: "10px 16px 10px 36px", borderRadius: 8,
                border: "1px solid #D8DADC", fontSize: 12, outline: "none",
                background: "#FAFAFA", fontFamily: F, boxSizing: "border-box",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#F3A78C")}
              onBlur={(e) => (e.target.style.borderColor = "#D8DADC")}
            />
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#818084", display: "flex", alignItems: "center" }}>
              <Search size={15} />
            </span>
          </div>
        </div>

        {/* ── Filter bar + inline stats ───────────────────────────────────────── */}
        <FilterBar>
          {[
            { k: "year",           l: "Year",            w: 90,  o: facets.year || [] },
            { k: "ite_event",      l: "ITE Event",       w: 150, o: facets.ite_event || [] },
            // Category options come from the selected event's taxonomy (not the data);
            // only shown once an event is chosen.
            ...(filters.ite_event && categoryOpts.length
              ? [{ k: "category", l: "Category", w: 200, o: categoryOpts }] : []),
            { k: "show",           l: "Competitor Show", w: 170, o: facets.show || [] },
            { k: "country",        l: "Country",         w: 120, o: facets.country || [] },
            { k: "country_status", l: "Country Status",  w: 130,
              o: CS_ORDER.filter((s) => (facets.country_status || []).includes(s)) },
            { k: "company_type",   l: "Company Type",    w: 140, o: facets.company_type || [] },
            { k: "hq_type",        l: "Entity Type",     w: 140, o: facets.hq_type || [] },
            { k: "priority",       l: "Priority",        w: 110, o: ["85+", "70–84", "Below 70"] },
            { k: "completeness",   l: "Completeness",    w: 160, o: ["Complete (≥70%)", "Needs enrichment (<70%)"] },
            { k: "website",        l: "Website",         w: 120, o: ["Has website", "No website"] },
          ].map((f) => (
            <FilterSelect
              key={f.k}
              value={filters[f.k] || ""}
              onChange={(e) => {
                const v = e.target.value;
                setFilters((p) => { const n = { ...p }; v ? (n[f.k] = v) : delete n[f.k]; return n; });
              }}
              active={!!filters[f.k]}
              style={{ width: f.w, flexShrink: 0 }}>
              <option value="">{f.l}</option>
              {f.o.map((o) => <option key={o} value={o}>{o}</option>)}
            </FilterSelect>
          ))}
          {hasFilters && <FilterClear onClick={() => setFilters({})} />}
          <div style={{ marginLeft: "auto", display: "flex", gap: 18, alignItems: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 10, color: "#818084" }}>
              <span style={{ fontWeight: 700, color: "#26262A", fontFamily: "'Montserrat',sans-serif" }}>{displayTotal}</span> companies
            </span>
            <span style={{ fontSize: 10, color: "#818084" }}>
              <span style={{ fontWeight: 700, color: "#C1121F", fontFamily: "'Montserrat',sans-serif" }}>{highPriority}</span> high priority
            </span>
            <span style={{ fontSize: 10, color: "#818084" }}>
              <span style={{ fontWeight: 700, color: "#E8431A", fontFamily: "'Montserrat',sans-serif" }}>{countryCount}</span> countries
            </span>
          </div>
        </FilterBar>

        {/* ── Body ───────────────────────────────────────────────────────────── */}
        <div ref={bodyRef} style={{ flex: 1, overflow: "auto", padding: "0 28px 32px" }}>

          {/* First load only — a refresh keeps the current rows, see <Refreshing> */}
          {initialLoading ? (
            <div style={{ textAlign: "center", padding: "80px 20px" }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#818084" }}>Loading companies…</div>
            </div>

          /* Error */
          ) : fetchError ? (
            <div style={{ textAlign: "center", padding: "80px 20px" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                <AlertCircle size={36} color="#C1121F" />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#3F3F46", marginBottom: 12 }}>
                {fetchError}
              </div>
              <button
                onClick={refresh}
                style={{
                  padding: "7px 20px", background: "#E8431A", color: "#fff",
                  border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", fontFamily: F,
                }}>
                Retry
              </button>
            </div>

          /* Empty */
          ) : sorted.length === 0 ? (
            <div style={{ textAlign: "center", padding: "80px 20px", color: "#818084" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
                <Search size={32} color="#818084" />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#818084", marginBottom: 4 }}>
                No companies found
              </div>
              <div style={{ fontSize: 11 }}>
                {isFiltered
                  ? "Try adjusting your search or filters"
                  : "No companies have been enriched yet"}
              </div>
              {isFiltered && (
                <button
                  onClick={() => { setSearch(""); setFilters({}); }}
                  style={{
                    marginTop: 14, padding: "6px 16px", background: "#F4F4F5",
                    border: "1px solid #D8DADC", borderRadius: 6, fontSize: 11,
                    fontWeight: 600, cursor: "pointer", fontFamily: F, color: "#3F3F46",
                  }}>
                  Clear all filters
                </button>
              )}
            </div>

          /* Card View */
          ) : viewMode === "card" ? (
            <Refreshing active={loading}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, paddingTop: 16, width: "100%", maxWidth: 1200, boxSizing: "border-box" }}>
              {sorted.map((c) => (
                <div
                  key={c.id}
                  onClick={() => navToCompany(c.id)}
                  style={{
                    background: "#fff", borderRadius: 10, border: "1px solid #D8DADC",
                    padding: "16px 18px", cursor: "pointer", transition: "all 0.15s",
                    display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden",
                    borderTop: `3px solid ${c.priority >= 70 ? "#22c55e" : c.priority >= 50 ? "#F18F18" : "#C1121F"}`,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>

                  {/* Name + country + status badge */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 6 }}>
                      <div
                        title={c.name}
                        style={{ fontSize: 13, fontWeight: 700, color: "#26262A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>
                        {c.name}
                      </div>
                      {c.derivedStatus === "Returning" && (
                        <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, background: "#FCEBE5",
                          color: "#E8431A", fontWeight: 700, border: "1px solid #F7C9B8",
                          whiteSpace: "nowrap", flexShrink: 0, marginTop: 1 }}>
                          ↩ Returning
                        </span>
                      )}
                      {c.derivedStatus === "New" && (
                        <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 4, background: "#f0fdf4",
                          color: "#16a34a", fontWeight: 700, border: "1px solid #bbf7d0",
                          whiteSpace: "nowrap", flexShrink: 0, marginTop: 1 }}>
                          ✦ New
                        </span>
                      )}
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:5, marginTop:1 }}>
                      <span style={{ fontSize:10, color:"#818084" }}>{c.country || "—"}</span>
                      <CountryStatusTag status={c.countryStatus} />
                    </div>
                  </div>

                  {/* Target ITE Event + Scores */}
                  <div style={{ marginBottom: 10, border: "1px solid #D8DADC", borderRadius: 8, overflow: "hidden" }}>
                    <div style={{ padding: "5px 10px", background: "#f0fdf4", borderBottom: "1px solid #d1fae5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ fontSize: 7, fontWeight: 700, color: "#166534", textTransform: "uppercase", letterSpacing: 0.5 }}>Target ITE Event</div>
                      <div
                        title={c.primaryEventName || "—"}
                        style={{ fontSize: 10, fontWeight: 700, color: "#14532d", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "70%", textAlign: "right" }}>
                        {c.primaryEventName || "—"}
                      </div>
                    </div>
                    <div style={{ display: "flex", background: "#fafafa" }}>
                      <div style={{ flex: 1, padding: "7px 6px", textAlign: "center", borderRight: "1px solid #F4F4F5" }}>
                        <div style={{ fontSize: 17, fontWeight: 800, fontFamily: M, lineHeight: 1, color: c.priority >= 70 ? "#16a34a" : c.priority >= 50 ? "#C9760A" : "#C1121F" }}>
                          {c.priority || "—"}
                        </div>
                        <div style={{ fontSize: 7, color: "#818084", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 2 }}>Priority Score</div>
                      </div>
                      <div style={{ flex: 1, padding: "7px 6px", textAlign: "center", borderRight: "1px solid #F4F4F5" }}>
                        <FitTag v={c.eventFit} />
                        <div style={{ fontSize: 7, color: "#818084", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 2 }}>Event Fit</div>
                      </div>
                      <div style={{ flex: 1, padding: "7px 6px", textAlign: "center" }}>
                        <FitTag v={c.exportReady} />
                        <div style={{ fontSize: 7, color: "#818084", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 2 }}>Export Readiness</div>
                      </div>
                    </div>
                  </div>

                  {/* Cross-Event Opportunities — always rendered to keep card heights uniform */}
                  <div style={{ marginBottom: 10, minHeight: 44 }}>
                    {c.secondaryEventNames.length > 0 ? (
                      <>
                        <div style={{ fontSize: 8, fontWeight: 700, color: "#818084", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                          Cross-Event Opportunities
                        </div>
                        <EventChips names={c.secondaryEventNames} />
                      </>
                    ) : (
                      <div style={{ fontSize: 8, color: "#D8DADC", marginTop: 4 }}>No cross-event opportunities</div>
                    )}
                  </div>

                  {/* Footer: completeness bar + link */}
                  <div style={{ paddingTop: 8, borderTop: "1px solid #F4F4F5", display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 8, color: "#818084" }}>Profile</span>
                      <CompBar pct={c.completeness} />
                    </div>
                    <span
                      onClick={(e) => { e.stopPropagation(); navToCompany(c.id); }}
                      style={{ fontSize: 10, color: "#E8431A", fontWeight: 600, cursor: "pointer" }}>
                      View Profile →
                    </span>
                  </div>
                </div>
              ))}
              <div style={{ gridColumn: "1 / -1", borderRadius: 10, border: "1px solid #D8DADC", overflow: "hidden" }}>
                <Pagination total={total} offset={offset} limit={PAGE_SIZE}
                            onChange={setOffset} disabled={loading} />
              </div>
            </div>
            </Refreshing>

          /* Table View */
          ) : (
            <Refreshing active={loading}>
            <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #D8DADC", overflow: "hidden", marginTop: 14 }}>
              {/* Table toolbar */}
              <div style={{ padding: "10px 14px", borderBottom: "1px solid #F4F4F5", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontSize: 10, color: "#818084" }}>
                  {displayTotal} {displayTotal === 1 ? "company" : "companies"} matching
                </span>
                {selectedIds.size > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#3F3F46" }}>
                      {selectedIds.size} selected
                    </span>
                    <button
                      onClick={() => setExportCfg({
                        title: "Export Selected Companies",
                        columns: COMPANY_MASTER_COLUMNS,
                        defaultKeys: DEFAULT_KEYS.companyMaster,
                        rows: sorted.filter((c) => selectedIds.has(c.id)),
                        filenameBase: "selected_companies",
                        exportType: "selected_companies",
                        scope: { filters },
                      })}
                      style={{
                        display: "flex", alignItems: "center", gap: 4, padding: "4px 10px",
                        background: "#E8431A", color: "#fff", border: "none", borderRadius: 5,
                        fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: F,
                      }}>
                      <Download size={10} /> Export selected
                    </button>
                    <button
                      onClick={() => setSelectedIds(new Set())}
                      style={{ padding: "4px 8px", background: "#F4F4F5", border: "1px solid #D8DADC", borderRadius: 5, fontSize: 10, color: "#3F3F46", cursor: "pointer", fontFamily: F }}>
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {/* Table */}
              <div style={{ overflowX: "auto" }}>
                <table className="ct">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "center", width: 36 }}>
                        <input
                          ref={allChkRef}
                          type="checkbox"
                          checked={sorted.length > 0 && selectedIds.size === sorted.length}
                          onChange={toggleAll}
                          style={{ cursor: "pointer" }}
                        />
                      </th>
                      {[
                        ["Company",                   "name",         true],
                        ["Country",                   "country",      true],
                        ["Target ITE Event",           null,           false],
                        ["Event Fit",                  "eventFit",     true],
                        ["Export Readiness",           "exportReady",  true],
                        ["Priority",                   "priority",     true],
                        ["Cross-Event",                null,           false],
                        ["Profile",                    "completeness", true],
                      ].map(([label, col, sortable]) => (
                        <th
                          key={label}
                          className={sortable && col ? "sortable" : ""}
                          onClick={sortable && col ? () => toggleSort(col) : undefined}>
                          {label}{sortable && col && <SortArrow col={col} />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((c) => (
                      <tr key={c.id} onClick={() => navToCompany(c.id)} style={{ cursor: "pointer" }}>
                        <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(c.id)}
                            onChange={(e) => toggleOne(c.id, e)}
                            style={{ cursor: "pointer" }}
                          />
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                            <div title={c.name} style={{ fontWeight: 600, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                            {c.derivedStatus === "Returning" && (
                              <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: "#FCEBE5", color: "#E8431A", fontWeight: 700, border: "1px solid #F7C9B8", whiteSpace: "nowrap", flexShrink: 0 }}>↩ R</span>
                            )}
                            {c.derivedStatus === "New" && (
                              <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: "#f0fdf4", color: "#16a34a", fontWeight: 700, border: "1px solid #bbf7d0", whiteSpace: "nowrap", flexShrink: 0 }}>✦ N</span>
                            )}
                          </div>
                        </td>
                        <td style={{ fontSize: 10, color: "#818084", whiteSpace: "nowrap" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                            <span>{c.country !== "—" ? c.country : <span style={{ color:"#D8DADC" }}>—</span>}</span>
                            <CountryStatusTag status={c.countryStatus} />
                          </div>
                        </td>
                        <td>
                          {c.primaryEventName
                            ? <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 4, background: "#f0fdf4", color: "#166534", fontWeight: 600, border: "1px solid #bbf7d0", whiteSpace: "nowrap", display: "inline-block", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }}>{c.primaryEventName}</span>
                            : <span style={{ color: "#D8DADC" }}>—</span>}
                        </td>
                        <td><FitTag v={c.eventFit} /></td>
                        <td><FitTag v={c.exportReady} /></td>
                        <td><ScoreBadge v={c.priority} /></td>
                        <td><EventChips names={c.secondaryEventNames} /></td>
                        <td><CompBar pct={c.completeness} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination total={total} offset={offset} limit={PAGE_SIZE}
                          onChange={setOffset} disabled={loading} />
            </div>
            </Refreshing>
          )}
        </div>
      </div>

      <ExportModal
        config={exportCfg}
        createdBy={currentUser || currentRole}
        onClose={() => setExportCfg(null)}
        onExported={(n) => showToast(`Exported ${n} ${n === 1 ? "company" : "companies"} → Excel`)}
      />
    </PageShell>
  );
}
